import {
  CareActionType,
  CareItem,
  careItems,
  getGrowthCapsulePrice,
} from './care.ruleset';
import { action } from '@play-co/replicant';
import createActions from 'src/replicant/config/actions/createActions';
import { getCurrencyGrantRandomBoxProperties } from '../analytics/events';
import { performCareAction, purchaseCareItem } from './care.modifiers';
import { PetStatusEffect } from '../pet';
import * as Pet from '../pet';
import * as Inventory from '../inventory';
import * as Fate from '../fate';
import { canPerformCareAction } from './care.getters';
import { sendAnalyticsEvents } from '../analytics';

export const careActions = createActions({
  /**
   * Purchase care items like: food, snacks, first_aid etc...
   */
  purchaseCareItem: action((state, careItemId: CareItem['id'], api) => {
    const events = [];
    const item = careItems[careItemId];
    const now = api.date.now();

    // Check if player can perform care action.
    if (!canPerformCareAction(state, item.type, now)) {
      return {
        consumed: false,
        bPointsConsumed: 0,
      } as const;
    }

    let { price } = item;

    // Consume inventory item first.

    const inventoryItems =
      Inventory.getters.getCareItems(state)[careItemId]?.count ?? 0;
    if (inventoryItems > 0) {
      Inventory.modifiers.consumeCareItems(state, careItemId, inventoryItems);
    }
    // Otherwise try to buy.
    else {
      if (Inventory.getters.getPoints(state) < price) {
        return {
          consumed: false,
          bPointsConsumed: 0,
        } as const;
      }

      Inventory.modifiers.consumePoints(
        state,
        price,
        {
          feature: 'care_action',
          $subFeature: careItemId,
        },
        api,
      );
    }

    const itemCount = Math.max(inventoryItems, 1);

    const results = {
      consumed: true,
      resolvedStatusEffect: false,
      targetStatusEffects: [] as PetStatusEffect[],
      bPointsConsumed: 0,
      effectiveMultiplier: { stomach: itemCount, love: itemCount },
      refund: { energy: 0 },
    };

    for (let i = 0; i < itemCount; i++) {
      const {
        events: resultsEvents,
        resolvedStatusEffect,
        targetStatusEffects,
        bPointsConsumed,
        refund,
      } = purchaseCareItem(
        state,
        careItemId,
        api.date.now(),
        api.math.random,
        inventoryItems > 0,
      );

      if (resolvedStatusEffect) {
        results.resolvedStatusEffect = resolvedStatusEffect;
      }
      if (targetStatusEffects) {
        results.targetStatusEffects.push(...targetStatusEffects);
      }
      results.bPointsConsumed += bPointsConsumed;

      // Add all events to the list.
      events.push(...resultsEvents);

      results.refund.energy += refund.energy;
    }

    if (results.refund.energy > 0) {
      Inventory.modifiers.addEnergy(
        state,
        results.refund.energy,
        now,
        {
          feature: 'random_box_spin',
          $subFeature: 'slot_machine_refund',
        },
        api,
        {
          excludeEnergy: true,
          getAdditionalProperties: (oldCurrencies, newCurrencies) =>
            getCurrencyGrantRandomBoxProperties({
              old: {
                energy: oldCurrencies.energy,
              },
              new: {
                energy: newCurrencies.energy,
              },
            }),
        },
      );
    }

    // Send all analytics events
    sendAnalyticsEvents(api, events);

    return results;
  }),

  performCareAction: action((state, type: CareActionType, api) => {
    const { events, ...results } = performCareAction(
      state,
      type,
      api.date.now(),
    );

    // Send all analytics events
    events && sendAnalyticsEvents(api, events);

    return results;
  }),

  purchaseGrowthCapsule: action((state, _: void, api) => {
    // No pet.
    const evolution = Pet.getters.getCurrentEvolution(state);
    if (!evolution) {
      throw new Error('Cannot purchase growth capsule without a pet.');
    }

    // Pet is already an adult.
    if (evolution.stage === 'adult') {
      throw new Error('Cannot purchase growth capsule for adult pet.');
    }

    // Not enough gems.
    const price = getGrowthCapsulePrice(evolution.stage);
    if (price > Inventory.getters.getGems(state)) {
      throw new Error('Not enough gems to purchase growth capsule.');
    }

    // Keep track of old currencies for analytics.
    const now = api.date.now();

    // Consume gems.
    Inventory.modifiers.consumeGems(
      state,
      price,
      {
        feature: 'growth_capsule',
        $subFeature: `growth_capsule_${evolution.stage}`,
      },
      api,
    );

    // Start evolution.
    Fate.modifiers.resolveStatusEffect(state, 'evolving', true, now);
  }),
});
